/**
 * Turns the four different listing data shapes into the one shape
 * ActivityCard renders, so no card component has to know where its data came
 * from.
 *
 * The listings are not consistent about field names:
 *
 *   lesson plans, grammar, reading, videos  flat items, with configurable
 *                                           categoryField / levelField / timeField
 *   vocabulary, speaking                    { title, level, desc, links, image }
 *                                           nested under { level, cards }
 *
 * `toCardModel` flattens both and resolves the colours, so the JS side of a
 * listing no longer carries its own colour maps. Category accents were three
 * separate hardcoded palettes (two CSS keyword maps in JS, one --cat-* block);
 * they now live in theme.css and are looked up here by slug.
 */

/** Category slug -> accent token. Anything unlisted falls back to the primary. */
const CATEGORY_ACCENTS = {
  // speaking-lesson topics
  business: 'var(--cat-business)',
  general: 'var(--cat-general)',
  food: 'var(--cat-food)',
  health: 'var(--cat-health)',
  travel: 'var(--cat-travel)',
  environment: 'var(--cat-environment)',
  education: 'var(--cat-education)',
  holidays: 'var(--cat-holidays)',
  airtraffic: 'var(--cat-airtraffic)',
  tech: 'var(--cat-tech)',
  money: 'var(--cat-money)',
  ai: 'var(--cat-ai)',
  listening: 'var(--cat-listening)',

  // page categories
  grammar: 'var(--skill-grammar)',
  reading: 'var(--skill-reading)',
  vocabulary: 'var(--skill-vocabulary)',
  speaking: 'var(--skill-speaking)',
  videos: 'var(--skill-videos)',
  quizzes: 'var(--skill-quizzes)',
  flashcards: 'var(--skill-flashcards)',
  writing: 'var(--skill-writing)',
};

/** "grammar-exercises" -> "grammar"; "Air Traffic" -> "airtraffic". */
export const categorySlug = (category) =>
  String(category || '')
    .toLowerCase()
    .replace(/-exercises?/g, '')
    .replace(/[^a-z0-9]+/g, '');

export const categoryAccent = (category) =>
  CATEGORY_ACCENTS[categorySlug(category)] || 'var(--color-primary)';

/** "grammar-exercises" -> "GRAMMAR", for the ribbon. */
export const categoryLabel = (category) => categorySlug(category).toUpperCase();

/**
 * Flat listing item -> card props.
 *
 * options:
 *   to            explicit link target, otherwise built from the item id
 *   categoryField default 'category'
 *   levelField    default 'level'
 *   timeField     default 'time'
 *   cta           action label; omit to hide the footer action
 */
export const toCardModel = (item = {}, options = {}) => {
  const {
    categoryField = 'category',
    levelField = 'level',
    timeField = 'time',
  } = options;

  const category = item[categoryField];
  const level = item[levelField];
  const id = item.id ?? item.title;

  return {
    to:
      options.to ??
      (id ? `/${category}/${encodeURIComponent(String(id).toLowerCase())}` : undefined),
    title: item.title,
    subtitle: item.subtitle || '',
    languagepoint: item.languagepoint || '',
    description: item.description || item.desc || '',
    image: item.image,
    category: category ? categoryLabel(category) : '',
    accent: categoryAccent(category),
    level,
    time: item[timeField],
    meta: item.meta || '',
    cta: options.cta,
    extraLinks: [],
  };
};

/**
 * Vocabulary/speaking card -> card props. These listings group cards under a
 * level heading and give each card a list of exercise links, which cannot sit
 * inside the card's own link, so they render underneath it.
 */
export const linkCardModel = (card = {}) => {
  const links = card.links || [];

  return {
    to: links.length === 1 ? links[0].to : undefined,
    title: card.title,
    subtitle: '',
    description: card.desc || '',
    image: card.image,
    category: '',
    accent: 'var(--color-primary)',
    level: card.level,
    time: '',
    meta: card.meta || '',
    cta: '',
    extraLinks: links.length > 1 ? links : [],
  };
};